import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Save, Trash2, ShieldCheck, Sparkles, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { EXPERIENCE, SPECIES, STYLES, WEAPONS, GOALS, BUDGETS, HUNT_LENGTHS, TRAVEL, DAYS, TERRAIN, DIFFICULTY, VISIBILITY } from "@/lib/hunterOptions";

const overline = "hr-overline text-blaze text-[11px]";
const sectionCls = "bg-white/[0.04] border border-white/10 rounded-xl p-6";

function Chip({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-sm font-semibold px-3.5 py-2 rounded-full border transition-all ${active ? "bg-blaze text-white border-blaze" : "bg-white/5 text-bone/70 border-white/15 hover:border-blaze/60"}`}>
      {active && <Check size={12} className="inline mr-1 -mt-0.5" />}{label}
    </button>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-bone/50 text-xs uppercase tracking-wider">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone" data-testid={testid}>
        <option value="" className="bg-charcoal">—</option>
        {options.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
      </select>
    </label>
  );
}

export default function MyHomeRangeData() {
  const { isAuthed, ready } = useAuth();
  const { openAuth } = useUI();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAuthed) api.homeRangeProfile().then(({ profile }) => {
      setForm({ ...profile, goals: (profile.goals || []).map((g) => g.custom_goal || g.goal_type).filter(Boolean) });
    });
  }, [isAuthed]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.saveHomeRangeProfile({
        ...form,
        goals: form.goals.map((g) => (GOALS.includes(g) ? { goal_type: g } : { goal_type: "custom", custom_goal: g })),
      });
      toast.success("Saved.");
    } catch { toast.error("Couldn't save."); }
    setSaving(false);
  };

  const toggleAI = async (enabled) => {
    set("ai_personalization_enabled", enabled);
    await api.saveHomeRangeProfile({ ai_personalization_enabled: enabled }).catch(() => {});
    toast(enabled ? "Personalized AI enabled." : "Personalized AI turned off.");
  };

  const clearAll = async () => {
    if (!window.confirm("Clear everything HomeRange remembers about your hunting preferences? This does not delete your hunts or points.")) return;
    const { profile } = await api.clearPersonalization();
    setForm({ ...profile, goals: (profile.goals || []).map((g) => g.custom_goal || g.goal_type).filter(Boolean) });
    toast.success("Personalization data cleared.");
  };

  if (!ready || (isAuthed && !form)) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;
  }

  if (!isAuthed) {
    return (
      <div className="bg-charcoal min-h-screen"><SiteHeader />
        <div className="max-w-lg mx-auto text-center py-24 px-5">
          <Lock className="text-blaze mx-auto" />
          <h1 className="font-serif text-4xl text-bone mt-5">My HomeRange Data</h1>
          <button onClick={() => openAuth("login")} className="mt-6 bg-blaze text-white px-6 py-3 rounded-full text-sm font-semibold" data-testid="data-signin">Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-charcoal min-h-screen grain" data-testid="homerange-data-page">
      <SiteHeader />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 text-bone">
        <Link to="/my-homerange" className="inline-flex items-center gap-2 text-sm text-bone/50 hover:text-bone transition-colors" data-testid="back-to-dashboard"><ArrowLeft size={15} /> My HomeRange</Link>
        <div className="mt-4 flex items-center gap-2"><Lock size={16} className="text-blaze" /><span className={overline}>Your data & privacy</span></div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2">My HomeRange Data</h1>
        <p className="text-bone/50 mt-2">Everything HomeRange remembers about you — view it, edit it, or clear it anytime. Financial and ZIP details are always private.</p>

        {/* Privacy controls */}
        <div className={`${sectionCls} mt-8`} data-testid="section-privacy">
          <div className="flex items-center gap-2"><ShieldCheck size={15} className="text-forest" /><span className={overline}>Privacy</span></div>
          <div className="mt-4 grid sm:grid-cols-2 gap-5">
            <Select label="Profile visibility" value={form.profile_visibility} options={VISIBILITY} onChange={(v) => set("profile_visibility", v)} testid="data-visibility" />
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded px-4 py-3">
              <div className="flex items-center gap-2"><Sparkles size={14} className="text-blaze" /><span className="text-sm text-bone/80">Personalized AI recommendations</span></div>
              <button role="switch" aria-checked={form.ai_personalization_enabled} onClick={() => toggleAI(!form.ai_personalization_enabled)} data-testid="data-ai-toggle"
                className={`relative w-11 h-6 rounded-full transition-colors ${form.ai_personalization_enabled ? "bg-blaze" : "bg-white/20"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.ai_personalization_enabled ? "translate-x-5" : ""}`} />
              </button>
            </div>
          </div>
          <p className="text-bone/40 text-xs mt-3">When off, HomeRange AI answers you like any visitor and won&apos;t use your saved profile.</p>
        </div>

        {/* Basics */}
        <div className={`${sectionCls} mt-5`} data-testid="section-basics">
          <span className={overline}>Basics</span>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="text-bone/50 text-xs uppercase tracking-wider">First name</span>
              <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-2 w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone" data-testid="data-first-name" /></label>
            <label className="block"><span className="text-bone/50 text-xs uppercase tracking-wider">Home state</span>
              <input value={form.home_state} onChange={(e) => set("home_state", e.target.value)} className="mt-2 w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone" data-testid="data-home-state" /></label>
            <Select label="Experience" value={form.experience_level} options={EXPERIENCE} onChange={(v) => set("experience_level", v)} testid="data-experience" />
            <label className="block"><span className="text-bone/50 text-xs uppercase tracking-wider flex items-center gap-1.5">ZIP <Lock size={10} /></span>
              <input value={form.zip_private} onChange={(e) => set("zip_private", e.target.value)} className="mt-2 w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone" data-testid="data-zip" /></label>
          </div>
        </div>

        {/* Preferences chip sections */}
        {[
          ["Species", "species", SPECIES],
          ["Hunting styles", "hunting_styles", STYLES],
          ["Weapons", "weapons", WEAPONS],
          ["States you hunt now", "states_hunt_now", null],
          ["States you want to hunt", "states_want", null],
          ["Goals", "goals", GOALS],
        ].map(([label, key, opts]) => (
          <div key={key} className={`${sectionCls} mt-5`} data-testid={`section-${key}`}>
            <span className={overline}>{label}</span>
            {opts ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {opts.map((o) => <Chip key={o} label={o} active={form[key].includes(o)} onClick={() => toggle(key, o)} testid={`data-${key}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} />)}
                {form[key].filter((v) => !opts.includes(v)).map((v) => (
                  <span key={v} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-full bg-forest text-bone">{v}<button onClick={() => toggle(key, v)}><X size={12} /></button></span>
                ))}
              </div>
            ) : (
              form[key].length ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {form[key].map((v) => (
                    <span key={v} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-full bg-white/5 border border-white/10 text-bone/80" data-testid={`data-${key}-${v.toLowerCase().replace(/\s+/g, "-")}`}>
                      {v}<button onClick={() => toggle(key, v)} className="hover:text-blaze" data-testid={`data-remove-${key}-${v.toLowerCase().replace(/\s+/g, "-")}`}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              ) : <p className="mt-3 text-bone/40 text-sm">None yet. Add these during onboarding or in Build My Hunt.</p>
            )}
          </div>
        ))}

        {/* Personalization detail */}
        <div className={`${sectionCls} mt-5`} data-testid="section-personalization">
          <span className={overline}>Personalization <Lock size={10} className="inline text-bone/40" /></span>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Select label="Annual budget (private)" value={form.annual_budget_private} options={BUDGETS} onChange={(v) => set("annual_budget_private", v)} testid="data-budget" />
            <Select label="Preferred hunt length" value={form.preferred_hunt_length} options={HUNT_LENGTHS} onChange={(v) => set("preferred_hunt_length", v)} testid="data-length" />
            <Select label="Travel willingness" value={form.travel_preference} options={TRAVEL} onChange={(v) => set("travel_preference", v)} testid="data-travel" />
            <Select label="Days afield / year" value={form.days_per_year} options={DAYS} onChange={(v) => set("days_per_year", v)} testid="data-days" />
            <Select label="Preferred terrain" value={form.preferred_terrain} options={TERRAIN} onChange={(v) => set("preferred_terrain", v)} testid="data-terrain" />
            <Select label="Physical difficulty" value={form.physical_difficulty} options={DIFFICULTY} onChange={(v) => set("physical_difficulty", v)} testid="data-difficulty" />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
          <button onClick={clearAll} className="inline-flex items-center gap-2 text-sm font-semibold text-red-300 hover:text-red-200 border border-red-400/30 hover:border-red-400/60 rounded-full px-5 py-3 transition-colors" data-testid="data-clear-all">
            <Trash2 size={15} /> Clear all personalization data
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-blaze text-white px-7 py-3 rounded-full font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="data-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
